import type { DataOption } from '@/common.types'
import type z from 'zod'

import { useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { map, paths, pipe } from 'lodash/fp'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useOptimizationGroupsQuery } from '@/entities/optimization-group'

import {
	FormProvider,
	RHFMultiSelect,
	RHFSelect,
	RHFTextField,
} from '@/shared/components/HookForm'
import { Button } from '@/shared/components/ui/button'
import {
	Modal,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { AnchorType } from '@/shared/generated'
import { useModals } from '@/shared/lib/modal-manager'
import { toOption } from '@/shared/tools/utils'

import { getAnchorSelectOptions } from '../lib/helpers'
import { getFormSchema } from '../model/contract'

type CreateAnchorStrategyModalProps = {
	close: () => void
	isOpen: boolean
}
export function CreateAnchorStrategyModal({
	close,
	isOpen,
}: CreateAnchorStrategyModalProps) {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const { data: optimizationGroupsOptions, isFetching } =
		useOptimizationGroupsQuery<DataOption[]>({
			select: pipe(map(paths(['id', 'name'])), toOption),
		})
	const methods = useForm({
		defaultValues: {
			optimization_group_ids: [],
			name: '',
			anchor_type: AnchorType.Flat,
		},
		resolver: zodResolver(getFormSchema(t)),
	})
	const _handleSubmit = (data: z.infer<ReturnType<typeof getFormSchema>>) => {
		showModal('ANCHOR_STRATEGY_MODAL', data)
		close()
	}
	const handleSubmit = methods.handleSubmit(_handleSubmit)
	const anchorSelectOptions = useMemo(() => getAnchorSelectOptions(t), [t])

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent
				data-testid='create-ahcnor-strategy-modal'
				className='max-w-xl'
				onSubmit={handleSubmit}
			>
				<ModalHeader>
					<ModalTitle>
						{t('anchoring_management.create_strategy.title')}
					</ModalTitle>
				</ModalHeader>
				<FormProvider
					methods={methods}
					onSubmit={handleSubmit}
					className='grid gap-6'
				>
					<RHFTextField
						className='w-full'
						autoComplete='off'
						label={t('anchoring_management.create_strategy.name.label')}
						name='name'
						placeholder={t(
							'anchoring_management.create_strategy.name.placeholder'
						)}
					/>
					<RHFSelect
						label={t('anchoring_type')}
						className='w-full'
						name='anchor_type'
						options={anchorSelectOptions}
					/>
					<RHFMultiSelect
						label={t('anchoring_management.create_strategy.ogs.title')}
						className='max-w-full overflow-x-auto'
						isLoading={isFetching}
						name='optimization_group_ids'
						options={(optimizationGroupsOptions as DataOption[]) || []}
					/>
					<ModalFooter className='justify-between'>
						<ModalClose asChild>
							<Button>{t('general_cancel')}</Button>
						</ModalClose>
						<Button type='submit' variant='primary'>
							{t('general_create')}
						</Button>
					</ModalFooter>
				</FormProvider>
			</ModalContent>
		</Modal>
	)
}
